// #3 tester (2026-10-21) — the dock's Go to post: a pending "show this post in its wall" request. The dock sets it
// and opens the wall (useGoToWallPost); the wall list showing that wall picks it up (useWallJump), scrolls to the
// post and highlights it, or hands it back as a fallback (the dock then opens the post dialog).
import { create, type StoreApi, type UseBoundStore } from "zustand";
import { wallRefOf, type WallPost, type WallRef } from "../api/content";
import { useAuthStore } from "./authStore";

export interface WallJump {
  post: WallPost;
  wall: WallRef;
  /** One per request: a stale timer or list never acts on a newer one. */
  nonce: number;
  /** A list showing this wall took it (it may still be paging). */
  claimed: boolean;
  /** The wall cannot show the post: open it as a dialog instead. */
  fallback: boolean;
}

export interface WallJumpState {
  target: WallJump | null;
  /** Replaces any pending request; returns its nonce. */
  request(post: WallPost): number;
  claim(nonce: number): void;
  fallBack(nonce: number): void;
  /** The request is finished (shown, or opened as a dialog). */
  done(nonce: number): void;
  /**
   * A list showing the jump's wall holds it while mounted; the returned release ends the jump once no list holds it
   * (the user left mid-paging). Deferred a tick, so StrictMode's effect replay re-holds it first. A fallback is kept:
   * the dock still opens it.
   */
  hold(nonce: number): () => void;
  reset(): void;
}

let seq = 0;
const holders = new Map<number, number>();

export const useWallJumpStore: UseBoundStore<StoreApi<WallJumpState>> = create<WallJumpState>()((set, get) => {
  const patch = (nonce: number, p: Partial<WallJump>) => {
    const t = get().target;
    if (t?.nonce === nonce) set({ target: { ...t, ...p } });
  };
  return {
    target: null,
    request(post) {
      const nonce = ++seq;
      set({ target: { post, wall: wallRefOf(post), nonce, claimed: false, fallback: false } });
      return nonce;
    },
    claim(nonce) {
      if (get().target?.nonce === nonce && !get().target?.claimed) patch(nonce, { claimed: true });
    },
    fallBack(nonce) {
      if (get().target?.nonce === nonce && !get().target?.fallback) patch(nonce, { claimed: true, fallback: true });
    },
    done(nonce) {
      if (get().target?.nonce === nonce) set({ target: null });
    },
    hold(nonce) {
      holders.set(nonce, (holders.get(nonce) ?? 0) + 1);
      return () => {
        holders.set(nonce, (holders.get(nonce) ?? 1) - 1);
        setTimeout(() => {
          if ((holders.get(nonce) ?? 0) > 0) return;
          holders.delete(nonce);
          const t = get().target;
          if (t?.nonce === nonce && !t.fallback) set({ target: null });
        }, 0);
      };
    },
    reset() {
      set({ target: null });
    },
  };
});

export const sameWall = (a: WallRef, b: WallRef): boolean =>
  a.type === "gc" ? b.type === "gc" && b.convId === a.convId : b.type === "user" && b.userId === a.userId;

// Logout or another account (same signal as wallPlaybackStore): the next user never inherits a pending jump.
useAuthStore.subscribe((s, prev) => {
  if ((prev.username && !s.username) || (prev.userId != null && s.userId !== prev.userId)) useWallJumpStore.getState().reset();
});
